import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Building2, User, Mail, Phone, MapPin, Loader2, AlertCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { validateEmail, validatePhone, formatPhoneNumber } from '@/lib/validations';
import {
  CREATE_ORGANIZATION,
  GET_ORGANIZATIONS,
  type CreateOrganizationInput,
} from '@/graphql/organizations';

type ClientType = 'CFI' | 'PSP';

interface ContactInfo {
  name: string;
  email: string;
  phone: string;
}

interface AddClientFormData {
  clientName: string;
  type: ClientType;
  website: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  primaryContact: ContactInfo;
  billingContact: ContactInfo;
  executiveContact: ContactInfo;
  billingSameAsPrimary: boolean;
  executiveSameAsPrimary: boolean;
}

const initialFormData: AddClientFormData = {
  clientName: '',
  type: 'CFI',
  website: '',
  phone: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  primaryContact: { name: '', email: '', phone: '' },
  billingContact: { name: '', email: '', phone: '' },
  executiveContact: { name: '', email: '', phone: '' },
  billingSameAsPrimary: false,
  executiveSameAsPrimary: false,
};

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: AddClientFormData) => void;
}

interface ContactSectionProps {
  title: string;
  contact: ContactInfo;
  onChange: (field: keyof ContactInfo, value: string) => void;
  sameAsPrimary?: boolean;
  onSameAsPrimaryChange?: (checked: boolean) => void;
  showSameAs?: boolean;
  emailError?: string;
  phoneError?: string;
  touched?: { email: boolean; phone: boolean };
  onBlur?: (field: 'email' | 'phone') => void;
}

function ContactSection({
  title,
  contact,
  onChange,
  sameAsPrimary,
  onSameAsPrimaryChange,
  showSameAs = false,
  emailError,
  phoneError,
  touched = { email: false, phone: false },
  onBlur,
}: ContactSectionProps) {
  const handleEmailChange = (value: string) => {
    onChange('email', value);
  };

  const handlePhoneChange = (value: string) => {
    // Auto-format phone number
    const formatted = formatPhoneNumber(value);
    onChange('phone', formatted);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate dark:text-mist">{title}</h3>
        {showSameAs && (
          <label className="flex items-center gap-2 text-xs text-chalkboard dark:text-rain-cloud">
            <input
              type="checkbox"
              checked={sameAsPrimary}
              onChange={(e) => onSameAsPrimaryChange?.(e.target.checked)}
              className="rounded border-gray text-pond focus:ring-pond dark:border-night-pond dark:bg-navy"
            />
            Same as Primary
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray" />
          <input
            type="text"
            placeholder="Full Name"
            value={contact.name}
            onChange={(e) => onChange('name', e.target.value)}
            disabled={sameAsPrimary}
            className="input pl-10 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray" />
            <input
              type="email"
              placeholder="Email Address"
              value={contact.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={() => onBlur?.('email')}
              disabled={sameAsPrimary}
              className={cn(
                'input pl-10 disabled:opacity-50 disabled:cursor-not-allowed',
                emailError && touched.email && !sameAsPrimary && 'border-rust dark:border-salmon focus:ring-rust'
              )}
            />
          </div>
          {emailError && touched.email && !sameAsPrimary && (
            <p className="mt-1 text-xs text-rust dark:text-salmon flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {emailError}
            </p>
          )}
        </div>
        <div>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray" />
            <input
              type="tel"
              placeholder="Phone Number (optional)"
              value={contact.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => onBlur?.('phone')}
              disabled={sameAsPrimary}
              className={cn(
                'input pl-10 disabled:opacity-50 disabled:cursor-not-allowed',
                phoneError && touched.phone && !sameAsPrimary && 'border-rust dark:border-salmon focus:ring-rust'
              )}
            />
          </div>
          {phoneError && touched.phone && !sameAsPrimary && (
            <p className="mt-1 text-xs text-rust dark:text-salmon flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {phoneError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Validation errors type for contacts
interface ContactValidationErrors {
  email?: string;
  phone?: string;
}

interface ContactTouched {
  email: boolean;
  phone: boolean;
}

const initialTouched: ContactTouched = { email: false, phone: false };

export function AddClientModal({ isOpen, onClose, onSubmit }: AddClientModalProps) {
  const [formData, setFormData] = useState<AddClientFormData>(initialFormData);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Validation state for each contact section
  const [primaryErrors, setPrimaryErrors] = useState<ContactValidationErrors>({});
  const [billingErrors, setBillingErrors] = useState<ContactValidationErrors>({});
  const [executiveErrors, setExecutiveErrors] = useState<ContactValidationErrors>({});
  const [primaryTouched, setPrimaryTouched] = useState<ContactTouched>(initialTouched);
  const [billingTouched, setBillingTouched] = useState<ContactTouched>(initialTouched);
  const [executiveTouched, setExecutiveTouched] = useState<ContactTouched>(initialTouched);

  // GraphQL mutation for creating organization
  const [createOrganization, { loading: isSubmitting }] = useMutation(CREATE_ORGANIZATION, {
    refetchQueries: [{ query: GET_ORGANIZATIONS }],
    onCompleted: (data) => {
      console.log('Organization created:', data.createOrganization);
      onSubmit?.(formData);
      handleClose();
    },
    onError: (error) => {
      console.error('Failed to create organization:', error);
      setErrorMessage(error.message || 'Failed to create client. Please try again.');
    },
  });

  const handleClose = () => {
    setFormData(initialFormData);
    setErrorMessage(null);
    // Reset validation state
    setPrimaryErrors({});
    setBillingErrors({});
    setExecutiveErrors({});
    setPrimaryTouched(initialTouched);
    setBillingTouched(initialTouched);
    setExecutiveTouched(initialTouched);
    onClose();
  };

  // Validation handlers for each contact type
  const handleContactBlur = (
    contactType: 'primary' | 'billing' | 'executive',
    field: 'email' | 'phone'
  ) => {
    const contact = contactType === 'primary' 
      ? formData.primaryContact 
      : contactType === 'billing'
        ? (formData.billingSameAsPrimary ? formData.primaryContact : formData.billingContact)
        : (formData.executiveSameAsPrimary ? formData.primaryContact : formData.executiveContact);
    
    const setTouched = contactType === 'primary' ? setPrimaryTouched 
      : contactType === 'billing' ? setBillingTouched 
      : setExecutiveTouched;
    
    const setErrors = contactType === 'primary' ? setPrimaryErrors 
      : contactType === 'billing' ? setBillingErrors 
      : setExecutiveErrors;
    
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    if (field === 'email' && contact.email) {
      const emailErr = validateEmail(contact.email);
      setErrors((prev) => ({ ...prev, email: emailErr }));
    } else if (field === 'phone' && contact.phone) {
      const phoneErr = validatePhone(contact.phone);
      setErrors((prev) => ({ ...prev, phone: phoneErr }));
    }
  };

  // Update errors when contact data changes (for touched fields)
  const updateContactWithValidation = (
    contactType: 'primaryContact' | 'billingContact' | 'executiveContact',
    field: keyof ContactInfo,
    value: string
  ) => {
    updateContact(contactType, field, value);
    
    const errorSetter = contactType === 'primaryContact' ? setPrimaryErrors
      : contactType === 'billingContact' ? setBillingErrors
      : setExecutiveErrors;
    
    const touched = contactType === 'primaryContact' ? primaryTouched
      : contactType === 'billingContact' ? billingTouched
      : executiveTouched;
    
    // Only validate if field has been touched
    if (field === 'email' && touched.email) {
      // Only show error for non-empty, allow empty since contacts are optional here
      if (value) {
        errorSetter((prev) => ({ ...prev, email: validateEmail(value) }));
      } else {
        errorSetter((prev) => ({ ...prev, email: undefined }));
      }
    } else if (field === 'phone' && touched.phone) {
      if (value) {
        errorSetter((prev) => ({ ...prev, phone: validatePhone(value) }));
      } else {
        errorSetter((prev) => ({ ...prev, phone: undefined }));
      }
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    // Build the GraphQL input
    const input: CreateOrganizationInput = {
      name: formData.clientName,
      type: formData.type,
      status: 'PROSPECT', // New clients start as prospects
      website: formData.website || undefined,
      phone: formData.phone || undefined,
    };

    // Execute the mutation - errors are handled by onError callback
    await createOrganization({
      variables: { input },
    });

    // Note: Contacts and addresses will be created separately
    // after the organization is created, in a future enhancement
  };

  const updateContact = (
    contactType: 'primaryContact' | 'billingContact' | 'executiveContact',
    field: keyof ContactInfo,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [contactType]: {
        ...prev[contactType],
        [field]: value,
      },
    }));
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={handleClose}
        disabled={isSubmitting}
        className="btn-secondary px-4 py-2"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!formData.clientName || isSubmitting}
        className="btn-primary"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Creating...
          </>
        ) : (
          'Create Client'
        )}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Client"
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Error Message */}
        {errorMessage && (
          <div className="p-3 bg-rust/10 border border-rust/20 rounded-lg">
            <p className="text-sm text-rust dark:text-salmon">{errorMessage}</p>
          </div>
        )}

        {/* Client Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate dark:text-mist flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Client Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                Client Name <span className="text-rust">*</span>
              </label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => setFormData((prev) => ({ ...prev, clientName: e.target.value }))}
                placeholder="Enter client name"
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                Type <span className="text-rust">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as ClientType }))}
                className="select"
              >
                <option value="CFI">CFI (Bank/Credit Union)</option>
                <option value="PSP">PSP (Professional Services Provider)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                placeholder="https://www.example.com"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Mailing Address */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate dark:text-mist flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Mailing Address
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: { ...prev.address, street: e.target.value },
                  }))
                }
                placeholder="123 Main Street"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value },
                  }))
                }
                placeholder="City"
                className="input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={formData.address.state}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      address: { ...prev.address, state: e.target.value },
                    }))
                  }
                  placeholder="IL"
                  className="input"
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-chalkboard dark:text-rain-cloud mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.address.zip}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      address: { ...prev.address, zip: e.target.value },
                    }))
                  }
                  placeholder="62701"
                  className="input"
                  maxLength={10}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contacts Section */}
        <div className="space-y-6 border-t border-gray dark:border-night-pond pt-6">
          <p className="text-xs text-chalkboard dark:text-rain-cloud">
            Contacts will be added after the organization is created.
          </p>
          
          <ContactSection
            title="Primary Contact"
            contact={formData.primaryContact}
            onChange={(field, value) => updateContactWithValidation('primaryContact', field, value)}
            emailError={primaryErrors.email}
            phoneError={primaryErrors.phone}
            touched={primaryTouched}
            onBlur={(field) => handleContactBlur('primary', field)}
          />

          <ContactSection
            title="Billing Contact"
            contact={formData.billingSameAsPrimary ? formData.primaryContact : formData.billingContact}
            onChange={(field, value) => updateContactWithValidation('billingContact', field, value)}
            showSameAs
            sameAsPrimary={formData.billingSameAsPrimary}
            onSameAsPrimaryChange={(checked) =>
              setFormData((prev) => ({ ...prev, billingSameAsPrimary: checked }))
            }
            emailError={billingErrors.email}
            phoneError={billingErrors.phone}
            touched={billingTouched}
            onBlur={(field) => handleContactBlur('billing', field)}
          />

          <ContactSection
            title="Executive Contact"
            contact={formData.executiveSameAsPrimary ? formData.primaryContact : formData.executiveContact}
            onChange={(field, value) => updateContactWithValidation('executiveContact', field, value)}
            showSameAs
            sameAsPrimary={formData.executiveSameAsPrimary}
            onSameAsPrimaryChange={(checked) =>
              setFormData((prev) => ({ ...prev, executiveSameAsPrimary: checked }))
            }
            emailError={executiveErrors.email}
            phoneError={executiveErrors.phone}
            touched={executiveTouched}
            onBlur={(field) => handleContactBlur('executive', field)}
          />
        </div>
      </div>
    </Modal>
  );
}
